import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Crown, CreditCard, Bell, HelpCircle, LogOut, ChevronRight, User,
  Mail, FileText, ShieldCheck, Headphones, Sparkles, MessageCircle,
  ExternalLink, CheckCircle2, Settings as SettingsIcon, Users as UsersIcon,
  Send, Loader2, Clock, ArrowDownToLine, Camera,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ArtistSocialsCard from "@/components/ArtistSocialsCard";

/**
 * AvatarUploader — small composable widget that lets a user replace
 * their profile picture in-place. Renders the current avatar (or
 * initial fallback) with a Camera-icon hover overlay that opens a
 * hidden file picker. Uploads to ``POST /api/auto-artwork/upload-picture``
 * (existing endpoint, JPG/PNG/WEBP ≤5 MB), then updates AuthContext so
 * the new picture shows immediately across every surface.
 */
const AvatarUploader = ({ user, setUser, size = 56 }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";                              // allow re-uploading same file
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error("Please pick a JPG, PNG or WEBP image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be ≤ 5 MB.");
      return;
    }
    const fd = new FormData();
    fd.append("picture", file);
    setUploading(true);
    try {
      const r = await api.post("/artwork/upload-picture", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Resolve to an absolute URL so React doesn't get confused by the
      // /api/uploads/cover/{file} relative path on cached renders. The
      // backend returns "/api/uploads/cover/<filename>".
      const newPic = r.data?.picture;
      if (newPic) {
        // Append a cache-bust so the <img> repaints immediately even when
        // the URL is identical to before (re-upload case).
        const cacheBusted = newPic + (newPic.includes("?") ? "&" : "?") + "t=" + Date.now();
        setUser({ ...user, picture: cacheBusted });
        toast.success("Profile picture updated.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed. Please try again.";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onPick}
      disabled={uploading}
      data-testid="avatar-upload-btn"
      className="group relative shrink-0 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-vermilion"
      style={{ width: size, height: size }}
      title="Upload a new profile picture"
    >
      {user?.picture ? (
        <img
          src={user.picture}
          alt={user.name || "You"}
          className="w-full h-full object-cover ring-2 ring-white/10"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#FF2E00]/40 to-[#FF2E00]/30 flex items-center justify-center text-xl font-semibold">
          {user?.name?.[0]?.toUpperCase() || "?"}
        </div>
      )}
      {/* Hover/uploading overlay */}
      <div className={`absolute inset-0 flex items-center justify-center bg-black/55 backdrop-blur-sm transition-opacity ${uploading ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
        {uploading ? (
          <Loader2 size={18} className="animate-spin text-white" />
        ) : (
          <Camera size={18} className="text-white" strokeWidth={2.4} />
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onChange}
        className="hidden"
        data-testid="avatar-upload-input"
      />
    </button>
  );
};

// ---------- Reusable row ----------
const Row = ({ icon: Icon, label, value, onClick, to, testId, accent, danger, badge }) => {
  const inner = (
    <>
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          danger
            ? "bg-[#FF2E00]/15 text-[#FF2E00]"
            : accent
            ? "bg-gradient-to-br from-[#FF2E00]/25 to-[#E2FF31]/15 text-white"
            : "bg-white/[0.06] text-white/80"
        }`}
      >
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-sm font-medium ${danger ? "text-[#FF2E00]" : "text-white"}`}>
            {label}
          </p>
          {badge && (
            <span className="text-[10px] uppercase tracking-wider bg-[#E2FF31]/15 text-[#E2FF31] px-1.5 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {value && <p className="text-xs text-white/45 truncate mt-0.5">{value}</p>}
      </div>
      {onClick || to ? (
        <ChevronRight size={16} className="text-white/30 group-hover:text-white/60 transition shrink-0" />
      ) : null}
    </>
  );

  const cls = `group w-full flex items-center gap-3 px-4 py-3 transition text-left ${
    onClick || to ? "hover:bg-white/[0.04] active:scale-[0.99]" : ""
  }`;

  if (to) {
    return (
      <Link to={to} data-testid={testId} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button data-testid={testId} onClick={onClick} className={cls} type="button">
      {inner}
    </button>
  );
};

const SectionCard = ({ title, children }) => (
  <div className="rounded-2xl bg-white/[0.03] border border-white/8 overflow-hidden">
    {title && (
      <p className="px-4 pt-4 pb-2 text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold">
        {title}
      </p>
    )}
    <div className="divide-y divide-white/5">{children}</div>
  </div>
);

// ---------- Payout History (artist-only) ----------
// Combines completed payouts from artist_payouts with queued requests from
// pending_payout_requests into a single timeline. Lets the artist verify
// every cashout — Stripe transfers, manual Venmo/Zelle entries, and the
// "queued, processing within 24-48 hours" pending state.
const PayoutHistorySection = () => {
  const [done, setDone] = React.useState([]);
  const [pending, setPending] = React.useState([]);
  const [pendingTotal, setPendingTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/connect/payouts", { params: { limit: 25 } }).catch(() => ({ data: { results: [] } })),
      api.get("/connect/my-pending").catch(() => ({ data: { queued: [], total_queued_usd: 0 } })),
    ]).then(([d, p]) => {
      if (cancel) return;
      setDone(d.data?.results || []);
      setPending(p.data?.queued || []);
      setPendingTotal(p.data?.total_queued_usd || 0);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  if (loading) {
    return (
      <div className="px-4 py-6 text-sm text-white/50">Loading payout history…</div>
    );
  }
  if (done.length === 0 && pending.length === 0) {
    return (
      <div className="px-4 py-8 text-center" data-testid="payout-history-empty">
        <ArrowDownToLine size={26} className="mx-auto text-white/25 mb-2" />
        <p className="text-sm text-white/55">No payouts yet</p>
        <p className="text-xs text-white/40 mt-1">
          Earn $5+ in streams to make your first cashout.
        </p>
      </div>
    );
  }

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
    } catch { return iso; }
  };

  const destLabel = (p) => {
    if (p.destination_type === "manual" || p.method === "manual" || p.method === "venmo" || p.method === "zelle") {
      return p.method ? p.method.charAt(0).toUpperCase() + p.method.slice(1) : "Manual";
    }
    if (p.destination_brand && p.destination_last4) {
      return `${p.destination_brand} •••• ${p.destination_last4}`;
    }
    if (p.destination_last4) return `•••• ${p.destination_last4}`;
    return "Bank";
  };

  return (
    <div className="divide-y divide-white/5" data-testid="payout-history">
      {pendingTotal > 0 && (
        <div className="px-4 py-3 bg-amber-300/[0.04] border-b border-amber-300/15">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-amber-300" />
              <p className="text-xs text-amber-200">
                <b className="text-amber-100">${pendingTotal.toFixed(2)} queued</b> — processing within 24–48 hrs
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">{pending.length}</span>
          </div>
        </div>
      )}
      {pending.map((p) => (
        <div
          key={p.request_id}
          data-testid={`payout-pending-${p.request_id}`}
          className="px-4 py-3 flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-amber-300/10 text-amber-300 flex items-center justify-center shrink-0">
              <Clock size={15} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">${p.amount_usd?.toFixed(2)}</p>
              <p className="text-xs text-white/45 truncate">Queued · {formatDate(p.created_at)} · {destLabel(p)}</p>
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-300 bg-amber-300/10 border border-amber-300/30 px-2 py-1 rounded-sm">
            Pending
          </span>
        </div>
      ))}
      {done.map((p, i) => (
        <div
          key={p.transfer_id || `${i}-${p.created_at}`}
          data-testid={`payout-done-${p.transfer_id || i}`}
          className="px-4 py-3 flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-emerald-400/10 text-emerald-300 flex items-center justify-center shrink-0">
              <ArrowDownToLine size={15} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">${(p.amount_usd ?? p.amount_usd_cents / 100).toFixed(2)}</p>
              <p className="text-xs text-white/45 truncate">{formatDate(p.created_at)} · {destLabel(p)}</p>
              {p.note && <p className="text-[10px] text-white/35 truncate mt-0.5">{p.note}</p>}
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-300 bg-emerald-300/10 border border-emerald-300/30 px-2 py-1 rounded-sm">
            {p.status === "failed" ? "Failed" : "Paid"}
          </span>
        </div>
      ))}
    </div>
  );
};

// ---------- Send-test-digest button (admin only, inside Admin Tools card) ----------
const SendTestDigestRow = () => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/digest/admin/send-test");
      if (r.data?.sent) {
        toast.success("Test digest emailed to you", {
          description: r.data?.email ? `Sent to ${r.data.email}` : "Check your inbox",
        });
      } else if (r.data?.scaffold_only) {
        toast.message("Digest ready — Resend key not set yet", {
          description:
            "Add RESEND_API_KEY to backend env to start sending. Everything else is wired and tested.",
        });
      } else {
        toast.error("Could not send test digest", {
          description: r.data?.reason || "Check backend logs",
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      data-testid="settings-send-test-digest"
      onClick={handle}
      disabled={busy}
      className="group w-full flex items-center gap-3 px-4 py-3 transition text-left hover:bg-white/[0.04] active:scale-[0.99] disabled:opacity-60"
    >
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E2FF31]/25 to-[#FF2E00]/15 text-white flex items-center justify-center shrink-0">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">
          {busy ? "Sending…" : "Send weekly digest to me"}
        </p>
        <p className="text-xs text-white/45 mt-0.5">
          Preview Friday&apos;s email — sent to your admin inbox right now
        </p>
      </div>
      <ChevronRight size={16} className="text-white/30 group-hover:text-white/60 transition shrink-0" />
    </button>
  );
};

// ---------- Weekly digest opt-in toggle (inline because it's the only stateful row) ----------
const DigestToggleRow = () => {
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/notifications/preferences")
      .then((r) => {
        setEnabled(r.data?.weekly_digest_enabled !== false);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const toggle = async () => {
    if (busy) return;
    const next = !enabled;
    setEnabled(next);
    setBusy(true);
    try {
      // Read current to preserve other flags, then merge
      const cur = await api.get("/notifications/preferences").catch(() => ({ data: {} }));
      await api.put("/notifications/preferences", {
        email_enabled: cur.data?.email_enabled !== false,
        push_enabled: cur.data?.push_enabled !== false,
        weekly_digest_enabled: next,
      });
      toast.success(next ? "Weekly digest enabled" : "Weekly digest paused");
    } catch (err) {
      setEnabled(!next);
      toast.error("Could not update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      data-testid="settings-weekly-digest-toggle"
      onClick={toggle}
      disabled={!loaded || busy}
      className="group w-full flex items-center gap-3 px-4 py-3 transition text-left hover:bg-white/[0.04] active:scale-[0.99]"
    >
      <div className="w-10 h-10 rounded-xl bg-white/[0.06] text-white/80 flex items-center justify-center shrink-0">
        <Mail size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">Weekly email digest</p>
        <p className="text-xs text-white/45 mt-0.5">
          Friday roundup — drops, fresh music, top 10, featured artist & Premium deals
        </p>
      </div>
      <div
        className={`w-10 h-6 rounded-full transition relative ${
          enabled ? "bg-[#E2FF31]" : "bg-white/15"
        }`}
      >
        <div
          className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${
            enabled ? "left-[18px]" : "left-0.5"
          }`}
        />
      </div>
    </button>
  );
};

const Settings = () => {
  const navigate = useNavigate();
  const { user, setUser, login, logout, premium } = useAuth();
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [claimCounts, setClaimCounts] = useState(null);

  useEffect(() => {
    if (!user) return;
    setCardsLoading(true);
    api
      .get("/premium/payment-methods")
      .then((r) => setCards(r.data?.results || []))
      .catch(() => setCards([]))
      .finally(() => setCardsLoading(false));
    // Check admin status — drives the Admin Tools section below
    api
      .get("/admin/whoami")
      .then((r) => setAdminInfo(r.data || null))
      .catch(() => setAdminInfo(null));
  }, [user]);

  // Fetch pending claim count badge for admins
  useEffect(() => {
    if (!adminInfo?.is_admin) {
      setClaimCounts(null);
      return;
    }
    api
      .get("/uploads/admin/artist-claims", { params: { status: "pending" } })
      .then((r) => setClaimCounts(r.data?.counts || null))
      .catch(() => setClaimCounts(null));
  }, [adminInfo?.is_admin]);

  if (!user) {
    return (
      <div data-testid="settings-page" className="flex flex-col items-center justify-center py-24 px-4 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF2E00]/30 to-[#FF2E00]/20 flex items-center justify-center mb-5">
          <SettingsIcon size={28} className="text-white" />
        </div>
        <h2 className="font-display text-3xl mb-2">Sign in to access settings</h2>
        <p className="text-white/55 text-sm max-w-md mb-6">
          Manage your subscription, payment methods, notifications, and more.
        </p>
        <button
          data-testid="settings-signin-btn"
          onClick={login}
          className="bg-white text-black rounded-full px-6 py-2.5 font-semibold hover:scale-[1.03] transition"
        >
          Sign in
        </button>
      </div>
    );
  }

  const tier = premium?.tier || "free";
  const isPremium = premium?.active;
  const cardCount = cards.length;
  const defaultCard = cards.find((c) => c.is_default) || cards[0];

  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await api.post("/premium/billing-portal", {
        return_url: window.location.origin + "/settings",
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open payment portal");
      setPortalBusy(false);
    }
  };

  return (
    <div
      data-testid="settings-page"
      className="pb-24 max-w-2xl mx-auto px-4 sm:px-0"
    >
      {/* Hero */}
      <div className="mb-8 pt-2">
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-2">Settings</p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter">
          Your account
        </h1>
      </div>

      {/* Profile card */}
      <div className="flex items-center gap-4 p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 mb-6">
        <AvatarUploader user={user} setUser={setUser} size={56} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{user.name}</p>
          <p className="text-xs text-white/50 truncate">{user.email}</p>
          <p className="text-[10px] uppercase tracking-[0.18em] text-vermilion/80 font-bold mt-1">
            Tap photo to change
          </p>
        </div>
        <Link
          to="/profile"
          data-testid="settings-edit-profile"
          className="text-xs text-white/60 hover:text-white px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 transition whitespace-nowrap"
        >
          Edit
        </Link>
      </div>

      {/* Artist socials self-service — only renders for verified artists */}
      <ArtistSocialsCard />

      {/* Subscription card */}
      <div className="p-5 rounded-2xl bg-white/[0.04] border border-white/10 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center ${
              isPremium
                ? "bg-gradient-to-br from-[#FF2E00] to-[#E2FF31] text-black"
                : "bg-white/10 text-white/50"
            }`}
          >
            <Crown size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold capitalize">
              {tier === "trial"
                ? "Free Trial"
                : tier === "artist"
                ? "Artist Premium"
                : tier === "premium"
                ? "Listener Premium"
                : "Free Plan"}
            </p>
            <p className="text-xs text-white/50">
              {isPremium
                ? `${premium.days_left} day${premium.days_left === 1 ? "" : "s"} remaining · $${premium?.price_usd?.toFixed(2)}/mo`
                : "No active subscription"}
            </p>
          </div>
          <Link
            to="/account"
            data-testid="settings-manage-subscription"
            className="text-xs font-semibold rounded-full px-4 py-2 bg-white/10 hover:bg-white/20 transition whitespace-nowrap"
          >
            Manage
          </Link>
        </div>
        {!isPremium && (
          <Link
            to="/premium"
            data-testid="settings-upgrade-cta"
            className="mt-3 block w-full text-center bg-[#FF2E00] hover:bg-[#E62900] text-white font-semibold rounded-full px-5 py-2.5 text-sm transition"
          >
            Get Premium
          </Link>
        )}
      </div>

      {/* Billing */}
      <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-2 px-1">
        Billing
      </p>
      <SectionCard>
        <Row
          icon={CreditCard}
          label="Payment methods"
          value={
            cardsLoading
              ? "Loading…"
              : cardCount === 0
              ? "No cards saved"
              : defaultCard
              ? `${defaultCard.brand?.toUpperCase()} •••• ${defaultCard.last4} (default)`
              : `${cardCount} card${cardCount === 1 ? "" : "s"} on file`
          }
          onClick={openBillingPortal}
          testId="settings-payment-methods"
          accent
        />
        <Row
          icon={FileText}
          label="Payment history"
          value="Receipts, refunds, downloads"
          to="/account"
          testId="settings-payment-history"
        />
        <Row
          icon={ExternalLink}
          label={portalBusy ? "Opening…" : "Open Stripe Customer Portal"}
          value="Update cards, view invoices, cancel anywhere"
          onClick={openBillingPortal}
          testId="settings-stripe-portal"
        />
      </SectionCard>

      {/* Admin tools — only for whitelisted admin emails (ADMIN_EMAILS env) */}
      {adminInfo?.is_admin && (
        <>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#E2FF31] font-semibold mb-2 mt-6 px-1 flex items-center gap-1.5">
            <ShieldCheck size={12} /> Admin Tools
          </p>
          <SectionCard>
            <Row
              icon={ShieldCheck}
              label="Artist claim requests"
              value={
                claimCounts?.pending
                  ? `${claimCounts.pending} pending review`
                  : "Approve or reject artist verification claims"
              }
              to="/admin/claims"
              testId="settings-admin-claims"
              accent
              badge={claimCounts?.pending ? String(claimCounts.pending) : null}
            />
            <Row
              icon={UsersIcon}
              label="All users"
              value="See who signed up and signed in"
              to="/admin/users"
              testId="settings-admin-users"
            />
            <Row
              icon={UsersIcon}
              label="Artist submissions"
              value="Pending rising-artist submissions"
              to="/admin/submissions"
              testId="settings-admin-submissions"
            />
            <SendTestDigestRow />
          </SectionCard>
        </>
      )}

      {/* Notifications */}
      <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-2 mt-6 px-1">
        Notifications
      </p>
      <SectionCard>
        <Row
          icon={Bell}
          label="Release alerts"
          value="Email + push when tracked artists release new music"
          to="/notifications"
          testId="settings-notifications"
        />
        <DigestToggleRow />
      </SectionCard>

      {/* Artist tools (only visible to verified/artist-tier users) */}
      {(tier === "artist" || premium?.tier === "artist") && (
        <>
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-2 mt-6 px-1">
            Artist Studio
          </p>
          <SectionCard>
            <Row
              icon={Sparkles}
              label="Manage uploads"
              value="Upload, edit, or delete your tracks"
              to="/upload"
              testId="settings-uploads"
              badge="Artist"
            />
            <Row
              icon={CheckCircle2}
              label="Stripe Connect (payouts)"
              value="$0.20 per stream — verified artists only"
              to="/account/payouts"
              testId="settings-payouts"
            />
          </SectionCard>

          {/* Payout history — completed + queued cashouts in one timeline */}
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-2 mt-6 px-1">
            Payout History
          </p>
          <div
            className="rounded-2xl bg-white/[0.03] border border-white/8 overflow-hidden"
            data-testid="settings-payout-history"
          >
            <PayoutHistorySection />
          </div>
        </>
      )}

      {/* Help */}
      <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-2 mt-6 px-1">
        Help &amp; Support
      </p>
      <SectionCard>
        <Row
          icon={HelpCircle}
          label="Help center"
          value="FAQs, troubleshooting, getting started"
          onClick={() => {
            document.getElementById("settings-faq")?.scrollIntoView({ behavior: "smooth" });
          }}
          testId="settings-help-center"
        />
        <Row
          icon={MessageCircle}
          label="Contact support"
          value="hello@1o1sound.app"
          onClick={() => (window.location.href = "mailto:hello@1o1sound.app?subject=1o1sound%20support")}
          testId="settings-contact"
        />
        <Row
          icon={ShieldCheck}
          label="Privacy & data"
          value="How we handle your account info"
          onClick={() => toast.message("We never sell your data. Music data comes from public sources (iTunes, Deezer, LRCLIB).")}
          testId="settings-privacy"
        />
      </SectionCard>

      {/* FAQ */}
      <div id="settings-faq" className="mt-8 mb-6 rounded-2xl bg-white/[0.03] border border-white/8 p-5 sm:p-6 scroll-mt-20">
        <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2">
          <Headphones size={18} className="text-[#E2FF31]" /> Frequently asked
        </h2>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <details
              key={i}
              data-testid={`settings-faq-${i}`}
              className="group rounded-xl bg-white/[0.03] border border-white/8 px-4 py-3 open:bg-white/[0.05] transition"
            >
              <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
                <span className="text-sm font-medium">{f.q}</span>
                <ChevronRight
                  size={16}
                  className="text-white/40 group-open:rotate-90 transition shrink-0"
                />
              </summary>
              <p className="text-sm text-white/60 mt-2 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      {/* Account actions */}
      <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-2 mt-6 px-1">
        Account
      </p>
      <SectionCard>
        <Row
          icon={User}
          label="Your public profile"
          value="What other listeners see"
          to="/profile"
          testId="settings-profile"
        />
        <Row
          icon={Mail}
          label="Email"
          value={user.email}
        />
        <Row
          icon={LogOut}
          label="Sign out"
          onClick={async () => {
            await logout();
            navigate("/");
          }}
          testId="settings-logout"
          danger
        />
      </SectionCard>

      <p className="text-center text-[11px] text-white/30 mt-8 px-4">
        1o1sound · Built for artists & listeners.
      </p>
    </div>
  );
};

const FAQ = [
  {
    q: "How do I get Premium?",
    a: "Go to the Premium tab. Listener Premium is $10/mo (ad-free, unlimited skips). Artist Premium is $25/mo with 3 months free — it unlocks uploads, payouts, and the Artist Studio.",
  },
  {
    q: "How do artist payouts work?",
    a: "Verified artists earn $0.20 per qualifying stream. Connect your bank in Account → Payouts. Withdrawals go through Stripe Express. Your own listens never count as streams.",
  },
  {
    q: "Why are some lyrics missing?",
    a: "Lyrics are sourced from LRCLIB (public, synced). If a song isn't in their database, the panel says so. Verified artists can submit their own lyrics for tracks on their claimed page.",
  },
  {
    q: "How do I claim my artist page?",
    a: "Open any artist page and tap 'Claim this page'. Artist Premium subscribers are auto-verified instantly; everyone else is reviewed within 24–48h.",
  },
  {
    q: "Where do I cancel?",
    a: "Go to Settings → Manage subscription, OR open the Stripe Customer Portal to cancel any time. You'll keep Premium until the end of the billing cycle.",
  },
  {
    q: "How do I schedule an upcoming release?",
    a: "Verified artists can tap 'Schedule release' on their artist page or any of their albums. Add a title, a release date, and it'll appear on Coming Soon for everyone tracking you.",
  },
];

export default Settings;
